import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { ThemeItem } from "../components/ThemeItem";
import { Typography } from "antd";

interface ThemeData {
  theme: string;
  description: string;
  activity_count?: number;
  activity_unit?: string;
  contributor_count?: number;
  change?: string;
  status?: string;
  keywords?: string[];
  evidence?: string[];
}

// Theme Block Recipe
const themeRecipe = {
  id: "theme-block",
  name: "Theme Block",
  globalKey: [],
  prefix: `[
    {
      "theme": "string",
      "description": "string — explain why the theme mattered",
      "activity_count": "number",
      "activity_unit": "string — e.g. 'comments', 'contributors', 'issues'",
      "contributor_count": "number — count of unique actors / contributors in this theme",
      "change": "string — e.g. '+18% vs prior week'",
      "status": "string — e.g. 'increasing', 'flat', 'declining', 'resurfacing'",
      "keywords": ["string"],
      "evidence": ["string"]  
        }
      ]`,
  format: {
    type: "json",
    fields: ["theme", "description", "activity_count", "status", "keywords"],
    constraints: [
      { field: "theme", type: "string", required: true },
      { field: "description", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as ThemeData;
          return Boolean(parsed.theme && parsed.description);
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// Theme Block Parser
function themeParser(data: any) {
  try {
    return {
      data: data[themeBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse theme data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// Theme Block Variants
const themeVariants = [
  {
    id: "pill",
    name: "Pill",
    description: "Pill theme display with all metrics",
    defaultSettings: {
      showMetrics: true,
      showKeywords: true,
      showEvidence: true,
      themeCardVariant: "pill" as const,
    },
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal theme display with only theme and description",
    defaultSettings: {
      showMetrics: false,
      showKeywords: false,
      showEvidence: false,
      themeCardVariant: "minimal" as const,
    },
  },
  {
    id: "default",
    name: "Default",
    description: "Default theme display with all metrics",
    defaultSettings: {
      showMetrics: true,
      showKeywords: true,
      showEvidence: true,
      themeCardVariant: "default" as const,
    },
  },
  {
    id: "full",
    name: "Full",
    description: "Full theme display with all metrics",
    defaultSettings: {
      showMetrics: true,
      showKeywords: true,
      showEvidence: true,
      themeCardVariant: "full" as const,
    },
  },

  {
    id: "compact",
    name: "Compact",
    description: "Compact theme display with all metrics",
    defaultSettings: {
      showMetrics: true,
      showKeywords: true,
      showEvidence: true,
      themeCardVariant: "compact" as const,
    },
  },
  {
    id: "expandable",
    name: "Expandable",
    description: "Expandable theme display with all metrics",
    defaultSettings: {
      showMetrics: true,
      showKeywords: true,
      showEvidence: true,
      themeCardVariant: "expandable" as const,
    },
  },
  {
    id: "list",
    name: "List",
    description:
      "List view with theme, contributor count, activity count, and activity unit",
    defaultSettings: {
      showMetrics: true,
      showKeywords: false,
      showEvidence: false,
      themeCardVariant: "list" as const,
    },
  },
];

// Theme Block Renderer
function ThemeBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const themeData = data[themeBlock.id];

  if (!themeData || !Array.isArray(themeData) || themeData.length === 0)
    return null;

  const totalContributionCount = themeData.reduce(
    (acc: number, theme: ThemeData) => {
      return acc + (theme.contributor_count ?? 0);
    },
    0
  );
  const variantSettings = themeVariants.find(
    (v) => v.id === variant
  )?.defaultSettings;

  if (variant === "list") {
    return (
      <div className="vzbl-panel-container">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px" }}>Theme</th>
              <th style={{ textAlign: "right", padding: "8px" }}>
                Contributors
              </th>
              <th style={{ textAlign: "right", padding: "8px" }}>Activity</th>
              <th style={{ textAlign: "left", padding: "8px" }}>Unit</th>
            </tr>
          </thead>
          <tbody>
            {themeData.map((theme: ThemeData, idx: number) => (
              <tr
                key={`theme-list-${idx}`}
                style={{ borderBottom: "1px solid #f0f0f0" }}
              >
                <td style={{ padding: "8px" }}>{theme.theme}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  {theme.contributor_count ?? 0}
                </td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  {theme.activity_count ?? 0}
                </td>
                <td style={{ padding: "8px" }}>
                  {theme.activity_unit || "activities"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      {themeData?.length > 0 && (
        <div className="vzbl-panel-container">
          <Typography.Title level={4} className="vzbl-header-4">
            Themes
          </Typography.Title>
          <div className="responsive-card-grid">
            {themeData.slice(0, 3).map((theme: ThemeData, idx: number) => (
              <div key={`theme-${idx}`} style={{ height: "100%" }}>
                <ThemeItem
                  theme={theme.theme}
                  description={theme.description}
                  activityCount={theme.activity_count || 0}
                  activityUnit={theme.activity_unit || "activities"}
                  contributorCount={theme.contributor_count || 0}
                  contributionPercentage={
                    theme.contributor_count
                      ? (theme.contributor_count / totalContributionCount) * 100
                      : 0
                  }
                  change={theme.change}
                  status={
                    theme.status as
                      | "increasing"
                      | "flat"
                      | "declining"
                      | "resurfacing"
                  }
                  keywords={theme.keywords}
                  variant={variantSettings?.themeCardVariant || "default"}
                  className="vzbl-panel-overlay vzbl-panel-shadow"
                  progressStrokeColor={{
                    from: "var(--color-blue-1)",
                    to: "var(--color-blue-2)",
                    direction: "to right",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// Create Theme Block
const themeBlock = createBaseBlock({
  id: "theme",
  name: "Theme Block",
  description: "Displays theme information with activity metrics",
  recipe: themeRecipe,
  variants: themeVariants,
  parser: themeParser,
  renderer: ThemeBlockRenderer,
});

// Export the block for direct use
export { themeBlock };
